import { FunctionComponent, useCallback, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Modal } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';

import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import { sendProtoInvoice } from '@payaca/store/proposals/proposalsActions';

type Props = {
  isOpen: boolean;
  protoInvoiceId: number;
  onClose: () => void;
  markAsSentCallback?: () => void;
};

const MarkProtoInvoiceAsSentModal: FunctionComponent<Props> = ({
  isOpen,
  protoInvoiceId,
  onClose,
  markAsSentCallback,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [errorMessages, setErrorMessages] = useState<string[]>();

  const [isMarkingAsSent, setIsMarkingAsSent] = useState(false);

  const onMarkAsSent = useCallback(() => {
    setIsMarkingAsSent(true);
    dispatch(
      sendProtoInvoice.request({
        protoInvoiceId: protoInvoiceId,
        data: {
          isMarkAsSent: true,
        },
        callback: () => {
          markAsSentCallback?.();
          setErrorMessages([]);
          onClose();
          setIsMarkingAsSent(false);
        },
        onErrorCallback: (errorMessages) => {
          setErrorMessages(errorMessages);
          setIsMarkingAsSent(false);
        },
      })
    );
  }, [protoInvoiceId, dispatch, markAsSentCallback, onClose]);

  return (
    <Modal isOpen={isOpen} title="Mark invoice as sent" onClose={onClose}>
      <Modal.Body>
        <p>
          This option is intended to be used in situations where you will send
          the invoice offline. For example - where you will print off the
          invoice and hand it to your customer on paper, in person.
        </p>
        <p>When an invoice has been marked as sent:</p>
        <ul>
          <li>No emails or other communication will go to the customer</li>
          <li>
            You will not be able to retrospectively send this invoice to the
            customer using the standard Payaca process
          </li>
          <li>
            If your account is connected to any third party services (Zapier,
            Xero, Quickbooks), they will treat this invoice as they would any
            other sent invoice
          </li>
          <li>Invoice reminders will not be sent</li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        {errorMessages?.map((errorMessage, i) => {
          return (
            <ErrorMessage
              message={errorMessage}
              key={`mark-as-sent-error-message-${i}`}
            />
          );
        })}
        <Modal.Footer.Actions>
          <Button
            onClick={onMarkAsSent}
            isProcessing={isMarkingAsSent}
            replaceContentWhenProcessing
          >
            Mark as sent without sending
            <UntitledIcon name="chevron-right" />
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

export default MarkProtoInvoiceAsSentModal;
